/**
 * AdminOrderDetailsPage
 * ------------------------------------------
 * This admin page displays full order details:
 *  • Shipping information (name, address, phone, email, carrier, tracking #)
 *  • Ordered items with product info
 *  • Enlarge product images in a modal
 *
 * Data comes from the Supabase tables:
 *  - shipping (linked by order_id)
 *  - order_items + joined Product table
 *
 * Admins can view:
 *  - Dimensions, weight, price, quantity, subtotal
 *  - Full-size product image
 *
 * No design changes were made — only a clean header comment added.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";

export default function AdminOrderDetailsPage() {
  const { orderId } = useParams();
  const [shipping, setShipping] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      const { data: shippingData } = await supabase
        .from("shipping")
        .select("*")
        .eq("order_id", orderId)
        .single();

      const { data: itemData } = await supabase
        .from("order_items")
        .select(`
        quantity,
        price_each,
        Product!order_items_product_id_fkey  (
          name,
          description,
          image_url,
          price,
          brandId,
          categoryId,
          weight,
          height,
          length,
          width
        )
      `)
        .eq("order_id", orderId);

      setShipping(shippingData);
      setItems(itemData || []);
      setLoading(false);
    };

    fetchOrderDetails();
  }, [orderId]);

  if (loading)
    return <div className="p-6 text-[#5c2f2f]">Loading order details...</div>;

  if (!shipping)
    return <div className="p-6 text-[#5c2f2f]">❌ No shipping info found.</div>;

  return (
    <div className="p-6 text-[#5c2f2f]">
      <h2 className="text-2xl font-bold mb-4">📦 Order Details</h2>

      <div className="bg-[#fff0f0] p-4 rounded shadow mb-6">
        <h3 className="text-lg font-semibold mb-2">Shipping Info</h3>
        <p><strong>Name:</strong> {shipping.to_name}</p>
        <p>
          <strong>Address:</strong> {shipping.to_address}, {shipping.to_city},{" "}
          {shipping.to_state} {shipping.to_zip}, {shipping.to_country}
        </p>
        {shipping.phone_number && (
          <p><strong>Phone:</strong> {shipping.phone_number}</p>
        )}
        {shipping.email && (
          <p><strong>Email:</strong> {shipping.email}</p>
        )}
        <p><strong>Carrier:</strong> {shipping.carrier}</p>
        <p><strong>Tracking #:</strong> {shipping.tracking_number || "N/A"}</p>
        <p><strong>Status:</strong> {shipping.status || "Pending"}</p>

        {shipping.label_url && (
          <a
            href={shipping.label_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-2 px-4 py-2 bg-[#5c2f2f] text-white rounded hover:bg-[#7a4343]"
          >
            Download Label
          </a>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">🛍 Ordered Products</h3>
        <div className="space-y-4">
          {items.map((item, idx) => (
            <div
              key={idx}
              className="flex gap-6 border border-[#eccccc] p-4 rounded shadow-sm bg-[#fffdfd]"
            >
              <img
                src={item.Product?.image_url}
                alt={item.Product?.name}
                className="w-56 h-56 object-contain rounded border cursor-pointer hover:shadow-lg transition"
                onClick={() => setSelectedImage(item.Product?.image_url)}
              />
              <div className="space-y-1 max-w-xl">
                <p className="text-lg font-bold">{item.Product?.name}</p>
                <p className="text-sm text-[#7a4343]">
                  {item.Product?.description}
                </p>
                <p>Brand ID: {item.Product?.brandId}</p>
                <p>Category ID: {item.Product?.categoryId}</p>
                <p>Weight: {item.Product?.weight} lb</p>
                <p>
                  Dimensions: {item.Product?.length} x {item.Product?.width} x{" "}
                  {item.Product?.height} in
                </p>
                <p>Unit Price: ${item.price_each}</p>
                <p>Quantity: {item.quantity}</p>
                <p className="font-semibold">
                  Subtotal: ${(item.price_each * item.quantity).toFixed(2)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center">
          <div className="relative">
            <img
              src={selectedImage}
              alt="Enlarged Product"
              className="max-w-[90vw] max-h-[90vh] rounded shadow-lg"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-2 right-2 bg-white text-black rounded-full w-8 h-8 flex items-center justify-center font-bold text-lg hover:bg-gray-200"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
